// @ts-nocheck
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useVideoMembershipAccess } from "@/hooks/useVideoMembershipAccess";
import { useAuth } from "@/hooks/useAuth";
import { usePurchaseMembership } from "@/hooks/usePurchaseMembership";
import { supabase } from "@/integrations/supabase/client";
import {
  Check,
  Star,
  Award,
  Trophy,
  Crown,
  ArrowRight,
  CheckCircle,
  Users,
  Target,
  TrendingUp,
  Eye,
  Clock,
  Globe,
  Zap,
  Shield,
  Heart,
  Video,
  UserPlus,
  FileText,
  Camera,
  Mic,
  ExternalLink,
  GraduationCap,
  Ruler,
  Mail,
  Phone
} from "lucide-react";

export default function Pricing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pressTerritories, setPressTerritories] = useState<{ sport: string; state: string }[]>([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(true);
  
  // Check for addon parameter
  const addon = searchParams.get('addon');
  const isVideoAddon = addon === 'video_hoops_recaps';
  
  // Video membership access hook
  const { hasAccess, requiredMembership, loading } = useVideoMembershipAccess(
    isVideoAddon ? 'video_hoops_recaps' : null
  );
  
  // Purchase membership hook
  const { purchaseMembership, isProcessing: isPurchasing } = usePurchaseMembership();
  
  // Handle video addon purchase
  const handleVideoPurchase = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    if (!requiredMembership) return;
    
    try {
      await purchaseMembership({
        membership_slug: requiredMembership.slug,
        billing_type: requiredMembership.billing_type as 'monthly' | 'one_time',
        price_cents: requiredMembership.price_cents,
        stripe_price_id: requiredMembership.stripe_price_id,
        is_addon: true,
        audience_type: 'athlete',
        source: 'video_addon_purchase'
      });
    } catch (error) {
      console.error('Error purchasing video addon:', error);
    }
  };
  
  // Fetch press territories on mount
  useEffect(() => {
    const fetchPressTerritories = async () => {
      setTerritoriesLoading(true);
      const { data } = await supabase
        .from('vendor_territories')
        .select('state, sport')
        .is('vendor_profile_id', null)
        .order('sport')
        .order('state');
      
      if (data) {
        setPressTerritories(data);
      }
      setTerritoriesLoading(false);
    };
    
    fetchPressTerritories();
  }, []);
  
  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const plans = [
    {
      name: "PROFILE",
      subtitle: "Free Membership",
      price: { monthly: "FREE", lifetime: "FREE" },
      icon: <Star className="w-12 h-12 text-white" />,
      bgGradient: "from-gray-600 to-gray-800",
      popular: false,
      features: [
        "Create athlete profile with all profile tools - Build a complete digital profile with personal info, stats, achievements, photos, videos, and contact details that coaches can view",
        "Appear in athlete search - Your profile shows up when coaches and scouts search for players by position, location, graduation year, or other criteria",
        "QR code flyer - Generate a scanable QR code that instantly links to your profile - perfect for business cards, flyers, or sharing at events",
        "Discount locker - Access exclusive discounts on recruiting camps, showcases, training equipment, and other recruiting-related services"
      ]
    },
    {
      name: "ATHLETE",
      subtitle: "Enhanced Features",
      price: { monthly: "$4.99", lifetime: "N/A" },
      icon: <Trophy className="w-12 h-12 text-white" />,
      bgGradient: "from-blue-600 to-blue-800",
      popular: true,
      features: [
        "AI Recruiting Helper - Smart assistant that provides personalized recruiting advice, email templates, and next-step recommendations",
        "Analytics - Track profile views, coach interactions, and engagement metrics to see which schools are interested",
        "College search - Advanced database to find colleges by academic programs, athletic divisions, location, and fit preferences",
        "Recruiting tracker - Organize communications with coaches, track interest levels, and manage your recruiting pipeline",
        "Camp listings - Browse and filter upcoming recruiting camps, showcases, and combines by location and sport",
        "Recruiting timeline - Interactive calendar showing key recruiting deadlines, contact periods, and important dates by grade level",
        "Scholarship database - Search academic and athletic scholarships with filters for eligibility requirements and deadlines",
        "Option for profile boost - Pay to feature your profile more prominently in search results for increased visibility",
        "Profile activity - Real-time notifications when coaches view your profile or when there's new interest",
        "Recruiting calendar - Personal calendar to track visits, calls, camps, and recruiting events you're attending",
        "Access to commitment graphics - Professional graphics and templates to announce your college commitment on social media"
      ]
    },
    {
      name: "PRESS",
      subtitle: "Media Coverage",
      price: { monthly: "$9.99", lifetime: "N/A" },
      icon: <FileText className="w-12 h-12 text-white" />,
      bgGradient: "from-green-600 to-green-800",
      popular: false,
      isPressPlan: true,
      pressHeader: [
        "Get All Access To Select State Coverage From Our RecruitLook Hoops Scouts",
        "*Each State / Territory is $9.99/Month",
        "Coverage includes:"
      ],
      features: territoriesLoading 
        ? ["Loading territories..."]
        : pressTerritories.length > 0
          ? pressTerritories.map(t => `${t.state} ${t.sport}`)
          : ["Coverage territories coming soon"]
    },
    {
      name: "SCOUT STARTER",
      subtitle: "Professional Guidance",
      price: { monthly: "$99.00", lifetime: "N/A" },
      icon: <Users className="w-12 h-12 text-white" />,
      bgGradient: "from-indigo-600 to-indigo-800",
      popular: false,
      features: [
        "Everything in Athlete Membership, plus:",
        "*Introductory email & profile tips from RecruitLook's National Scout",
        "*A Monthly 60-minute call with RecruitLook's National Scout (unused minutes don't transfer month-to-month)",
        "*Monthly Profile checkover from RecruitLook's National Scout"
      ]
    },
    {
      name: "MVP",
      subtitle: "Personal Scout Support",
      price: { monthly: "$249.00", lifetime: "N/A" },
      icon: <Award className="w-12 h-12 text-white" />,
      bgGradient: "from-yellow-500 to-yellow-700",
      popular: false,
      features: [
        "Everything in Athlete & Starter Memberships, plus:",
        "*A Monthly 120-minute call with RecruitLook's National Scout (unused minutes don't transfer month-to-month)",
        "*Film Session - RecruitLook's National Scout will review 1 video per month with feedback"
      ]
    },
    {
      name: "HALL OF FAME",
      subtitle: "Premium Coaching",
      price: { monthly: "N/A", lifetime: "$3,995.00" },
      icon: <Crown className="w-12 h-12 text-white" />,
      bgGradient: "from-purple-600 to-purple-800",
      popular: false,
      lifetimeOnly: true,
      features: [
        "All MVP features included",
        "Assistance contacting college coaches",
        "Target list creation assistance",
        "Unlimited RecruitLook Scout access",
        "Recruiting video review & feedback",
        "Priority customer support",
        "Advanced analytics dashboard",
        "Personalized recruiting strategy",
        "Direct coach connections",
        "Guaranteed response times"
      ]
    },
    {
      name: "LAST CHANCE",
      subtitle: "Final Year Push",
      price: { monthly: "N/A", lifetime: "$399.00" },
      icon: <Zap className="w-12 h-12 text-white" />,
      bgGradient: "from-red-600 to-red-800",
      popular: false,
      lifetimeOnly: true,
      features: [
        "All Athlete features included",
        "Accelerated recruiting timeline",
        "Priority scout attention",
        "Urgent coach outreach",
        "Last-minute opportunity alerts",
        "Expedited profile processing",
        "Emergency recruiting support",
        "Final year strategy planning",
        "Immediate coach connections"
      ]
    }
  ];

  // Add video addon as a plan if the addon parameter is present
  const videoAddonPlan = isVideoAddon && requiredMembership ? {
    name: "HOOPS RECAPS",
    subtitle: "Premium Basketball Video Content",
    price: { 
      monthly: formatPrice(requiredMembership.price_cents), 
      lifetime: formatPrice(requiredMembership.price_cents) 
    },
    icon: <Video className="w-12 h-12 text-white" />,
    bgGradient: "from-purple-600 to-purple-800",
    popular: false,
    isVideoAddon: true,
    features: [
      "Full game recap videos",
      "Key highlight reels", 
      "Expert game analysis",
      "Player performance breakdowns",
      "Exclusive behind-the-scenes content"
    ]
  } : null;

  const allPlans = videoAddonPlan ? [videoAddonPlan, ...plans] : plans;

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/lovable-uploads/9502bc17-7982-4ac0-8414-2a599aa99bdf.png"
            alt="Sports balls collection"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-secondary text-black px-6 py-3 text-sm font-black tracking-wider mb-8">
            MEMBERSHIP PACKAGES
          </Badge>
          <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight">
            CHOOSE YOUR
            <span className="block text-secondary">RECRUITING PLAN</span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12">
            RecruitLook Memberships are available at both monthly and one-time purchase rates. 
            Monthly packages are primarily DIY and our one-time payment packages are SCOUT-led programs with your own RecruitLook Scout.
          </p>

        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allPlans.map((plan: any, index: number) => (
                  <div key={plan.name} className="relative">
                    {(plan.popular || plan.isVideoAddon) && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                        <Badge className="bg-red-600 text-white px-4 py-2 font-black text-xs">
                          {plan.isVideoAddon ? "PREMIUM CONTENT" : "MOST POPULAR"}
                        </Badge>
                      </div>
                    )}
                    
                    <div className="bg-white rounded-2xl overflow-hidden shadow-lg border-2 border-gray-200 hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
                      {/* Plan Header */}
                      <div className={`bg-gradient-to-br ${plan.bgGradient} p-6 text-center text-white relative`}>
                        <div className="mb-4">
                          {plan.icon}
                        </div>
                        <h3 className="text-2xl font-black mb-2">{plan.name}</h3>
                        <p className="text-sm opacity-90 mb-4">{plan.subtitle}</p>
                        
                        <div className="text-center space-y-3">
                          {/* Monthly Price */}
                          {plan.price.monthly !== "N/A" && (
                            <div>
                              <div className="text-xs opacity-80 uppercase mb-1">Monthly</div>
                              <div className="text-2xl font-black">
                                {plan.price.monthly}
                                {plan.price.monthly !== "FREE" && !plan.isVideoAddon && (
                                  <span className="text-base font-normal opacity-80">/month</span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Lifetime/One-time Price */}
                          {plan.price.lifetime !== "N/A" && plan.price.lifetime !== plan.price.monthly && (
                            <div>
                              <div className="text-xs opacity-80 uppercase mb-1">
                                {plan.lifetimeOnly ? "One-time" : "Lifetime"}
                              </div>
                              <div className="text-2xl font-black">
                                {plan.price.lifetime}
                                <span className="text-base font-normal opacity-80"> one-time</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Video Addon Price */}
                          {plan.isVideoAddon && requiredMembership && (
                            <div>
                              <div className="text-2xl font-black">
                                {formatPrice(requiredMembership.price_cents)}
                                <span className="text-base font-normal opacity-80">
                                  {requiredMembership.billing_type === 'monthly' ? '/month' : ' one-time'}
                                </span>
                              </div>
                              <div className="text-xs opacity-80">
                                {requiredMembership.billing_type === 'monthly' ? 'Monthly subscription' : 'One-time payment'}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Features List */}
                      <div className="p-6 flex-grow">
                        <TooltipProvider>
                          {/* Press plan header (without checkmarks) */}
                          {plan.pressHeader && (
                            <div className="mb-4 space-y-2 text-center">
                              {plan.pressHeader.map((text: string, idx: number) => (
                                <p key={idx} className="text-sm text-gray-700 font-semibold">
                                  {text}
                                </p>
                              ))}
                            </div>
                          )}
                          
                          <ul className="space-y-3">
                            {plan.features.map((feature: string, featureIndex: number) => {
                              const [title, description] = feature.includes(' - ') 
                                ? feature.split(' - ', 2)
                                : [feature, ''];
                              
                              return (
                                <li key={featureIndex} className="flex items-start space-x-3">
                                  <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                  {description ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="text-sm text-gray-700 cursor-help underline decoration-dotted underline-offset-2">
                                          {title}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs">
                                        <p>{description}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : (
                                    <span className="text-sm text-gray-700">{title}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </TooltipProvider>
                      </div>

                      {/* CTA Button */}
                      <div className="p-6 pt-0">
                        {plan.isVideoAddon ? (
                          loading ? (
                            <Button disabled className="w-full font-black py-3 bg-gray-400 text-white">
                              CHECKING ACCESS...
                            </Button>
                          ) : hasAccess ? (
                            <Button 
                              onClick={() => navigate('/videos/hoops_recaps/watch')}
                              className="w-full font-black py-3 bg-green-600 hover:bg-green-700 text-white"
                            >
                              WATCH VIDEOS
                              <Video className="ml-2 w-4 h-4" />
                            </Button>
                          ) : !user ? (
                            <Button 
                              onClick={() => navigate('/auth')}
                              className="w-full font-black py-3 bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              LOGIN TO PURCHASE
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              onClick={handleVideoPurchase}
                              disabled={isPurchasing}
                              className="w-full font-black py-3 bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              {isPurchasing ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  PROCESSING...
                                </>
                              ) : (
                                <>
                                  PURCHASE ACCESS
                                  <ArrowRight className="ml-2 w-4 h-4" />
                                </>
                              )}
                            </Button>
                          )
                        ) : (
                          <Button 
                            onClick={() => navigate('/login')}
                            className={`w-full font-black py-3 ${
                              plan.popular 
                                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                : 'bg-slate-900 hover:bg-slate-800 text-white'
                            }`}
                          >
                            SIGN IN
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>
    </div>
  );
}